import { Hidden } from "@material-ui/core";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ReactHtmlParser from "react-html-parser";
import sanitizeHtml from "sanitize-html";
import { boolean } from "yup";
import { Toggle } from "../../../../../components/forms/toggle";
import { ShowMore } from "../../../../../components/showmore";
import { Field } from "../../internal/field";
import {
  ConfirmText,
  ErrorText,
  FieldHook,
  FullWidthItem,
  RenderFuncProps,
  ToggleWrapper,
} from "../common";
import { useController, useFormContext } from "react-hook-form";

const Elem: FC<RenderFuncProps> = ({ item }) => {
  const { t, i18n } = useTranslation("fields");
  const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
    i18n,
  ]);
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name: item.field,
  });

  const innerDescription = useMemo(
    () => (
      <div>
        <div>
          <strong>{t("declaration-statements")}</strong>
        </div>
        {error?.message ? <ErrorText>{error.message}</ErrorText> : null}
        {Field.isDeclarationStatementsField(item) ? (
          <ShowMore lines={3}>
            {ReactHtmlParser(
              item.desc?.[currentLanguage]
                ? sanitizeHtml(item.desc[currentLanguage])
                : ""
            )}
          </ShowMore>
        ) : null}
      </div>
    ),
    [t, error]
  );

  const innerToggle = useMemo(() => {
    return (
      <ToggleWrapper>
        <Toggle {...field} />
        <ConfirmText ticked={field.value}>{t("i-confirm")}</ConfirmText>
      </ToggleWrapper>
    );
  }, [field, t]);

  return (
    <>
      <Hidden smDown>
        {innerToggle}
        {innerDescription}
      </Hidden>
      <Hidden mdUp>
        <FullWidthItem>
          {innerToggle}
          {innerDescription}
        </FullWidthItem>
      </Hidden>
    </>
  );
};

const useSchema = () => {
  const { t } = useTranslation("fields");
  const ERROR_TEXT = useMemo(() => t("you-must-agree-to-the-statements"), [t]);

  return useCallback(
    (f: Field.DefaultField) =>
      (f.optional
        ? boolean()
        : boolean().required(ERROR_TEXT).isTrue(ERROR_TEXT)
      ).label(t("declaration-statements")),
    [t, ERROR_TEXT]
  );
};

export const useDeclarationStatementsField: FieldHook = () => {
  const schema = useSchema();
  return useMemo(
    () => ({
      skipOptionalCheck: true,
      schema,
      elem: Elem,
    }),
    [schema]
  );
};
